// Path: C:\gestion-copro\src\pages\admin-saas\DashboardCommissionsPage.tsx
// Session 225 : Ajout onglet Guide + bandeau alerte users actifs sans apporteurId

import React, { useState, useEffect, useCallback } from 'react';
import {
  Euro,
  RefreshCw,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
  Coins,
  CalendarDays,
  BadgeCheck,
  AlertCircle,
  TrendingUp,
  BookOpen,
  ListChecks,
  ArrowRight,
  UserX,
} from 'lucide-react';
import {
  Commission,
  StatutCommission,
  LABELS_STATUT_COMMISSION,
  TAUX_COMMISSION,
  InfoResponsable,
} from '../../types/crm.types';
import {
  chargerCommissions,
  enregistrerCommission,
  marquerCommissionVersee,
  chargerResponsables,
  calculerCommissionsTrimestre,
  chargerUsersActifsSansApporteur,
} from '../../services/crm.service';

// ============================================
// HELPERS
// ============================================

const trimestreActuel = (): string => {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-T${q}`;
};

const trimestresDisponibles = (): string[] => {
  const result: string[] = [];
  const now = new Date();
  for (let i = 0; i < 8; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i * 3, 1);
    const q = Math.ceil((d.getMonth() + 1) / 3);
    result.push(`${d.getFullYear()}-T${q}`);
  }
  return Array.from(new Set(result));
};

const formatEur = (n: number) =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const COULEURS_STATUT: Record<StatutCommission, string> = {
  du: 'bg-amber-100 text-amber-800 border-amber-300',
  partiel: 'bg-blue-100 text-blue-800 border-blue-300',
  verse: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

// ============================================
// BANDEAU ALERTE USERS SANS APPORTEUR
// ============================================

interface UserSansApporteur {
  userId: string;
  email: string;
  displayName: string;
  formule: string;
}

const BandeauAlerteSansApporteur: React.FC<{ users: UserSansApporteur[] }> = ({ users }) => {
  const [ouvert, setOuvert] = useState(false);

  if (users.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <UserX className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">
              {users.length} abonné{users.length > 1 ? 's' : ''} actif{users.length > 1 ? 's' : ''} sans apporteur lié
            </p>
            <p className="text-sm text-amber-700 mt-0.5">
              {users.length > 1 ? 'Ces abonnés ne sont' : "Cet abonné n'est"} rattaché{users.length > 1 ? 's' : ''} à aucun apporteur — {users.length > 1 ? 'leurs commissions ne seront' : 'sa commission ne sera'} pas comptabilisée{users.length > 1 ? 's' : ''}.
              Utilise la liaison manuelle dans la fiche apporteur pour corriger.
            </p>
          </div>
        </div>
        <button
          onClick={() => setOuvert(!ouvert)}
          className="text-amber-600 hover:text-amber-800 transition-colors flex-shrink-0"
        >
          {ouvert ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {ouvert && (
        <div className="mt-3 border-t border-amber-200 pt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-amber-600 uppercase">
                <th className="text-left pb-1">Nom / Email</th>
                <th className="text-left pb-1">Formule</th>
                <th className="text-right pb-1">ID Firebase</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-100">
              {users.map((u) => (
                <tr key={u.userId}>
                  <td className="py-1.5">
                    <p className="font-medium text-amber-900">{u.displayName}</p>
                    <p className="text-xs text-amber-600">{u.email}</p>
                  </td>
                  <td className="py-1.5 text-amber-800">{u.formule}</td>
                  <td className="py-1.5 text-right font-mono text-xs text-amber-500">{u.userId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPOSANT CARTE COMMISSION
// ============================================

interface CarteCommissionProps {
  commission: Commission;
  onMarquerVerse: (commission: Commission) => void;
}

const CarteCommission: React.FC<CarteCommissionProps> = ({ commission, onMarquerVerse }) => {
  const [ouvert, setOuvert] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <Coins className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-800 truncate">{commission.apporteurNom}</p>
            <p className="text-sm text-gray-500">{commission.trimestre}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <span className={`text-xs px-2 py-1 rounded-full border font-medium ${COULEURS_STATUT[commission.statut]}`}>
            {LABELS_STATUT_COMMISSION[commission.statut]}
          </span>

          <div className="text-right">
            <p className="font-bold text-gray-900">{formatEur(commission.montantDu)}</p>
            {commission.statut !== 'verse' && (
              <p className="text-xs text-amber-600">
                Reste : {formatEur(commission.resteAVerser)}
              </p>
            )}
          </div>

          {commission.statut !== 'verse' && (
            <button
              onClick={() => onMarquerVerse(commission)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <CheckCircle2 className="w-4 h-4" />
              Verser
            </button>
          )}
          {commission.statut === 'verse' && (
            <div className="flex items-center gap-1 text-emerald-600 text-sm">
              <BadgeCheck className="w-4 h-4" />
              Versé
            </div>
          )}

          <button
            onClick={() => setOuvert(!ouvert)}
            className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {ouvert ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {ouvert && (
        <div className="border-t border-gray-100 p-4">
          {commission.details.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Aucun client actif ce trimestre.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b border-gray-100">
                  <th className="text-left pb-2">Client</th>
                  <th className="text-left pb-2">Formule</th>
                  <th className="text-left pb-2">Statut</th>
                  <th className="text-right pb-2">CA HT/mois</th>
                  <th className="text-right pb-2">Commission/mois</th>
                  <th className="text-right pb-2">Commission trimestre</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {commission.details.map((d) => (
                  <tr key={d.userId} className="py-2">
                    <td className="py-2 font-medium text-gray-800">{d.nomClient}</td>
                    <td className="py-2 text-gray-600">{d.formule || 'Non renseigné'}</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        d.statutAbonnement === 'actif'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {d.statutAbonnement}
                      </span>
                    </td>
                    <td className="py-2 text-right text-gray-700">{formatEur(d.caHTMensuel)}</td>
                    <td className="py-2 text-right text-gray-700">{formatEur(d.commissionMensuelle)}</td>
                    <td className="py-2 text-right font-semibold text-indigo-700">
                      {formatEur(d.commissionMensuelle * 3)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200">
                  <td colSpan={5} className="pt-2 text-right text-sm font-semibold text-gray-700">Total trimestre :</td>
                  <td className="pt-2 text-right font-bold text-indigo-800">{formatEur(commission.montantDu)}</td>
                </tr>
              </tfoot>
            </table>
          )}

          {commission.dateVersement && (
            <p className="mt-3 text-xs text-gray-400">
              Versé le {commission.dateVersement.toDate().toLocaleDateString('fr-FR')}
              {commission.referenceVersement && ` — Réf. ${commission.referenceVersement}`}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// MODAL VERSEMENT
// ============================================

interface ModalVersementProps {
  commission: Commission;
  onConfirmer: (montant: number, reference: string) => Promise<void>;
  onFermer: () => void;
}

const ModalVersement: React.FC<ModalVersementProps> = ({ commission, onConfirmer, onFermer }) => {
  const [montant, setMontant] = useState(commission.resteAVerser.toString());
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirmer = async () => {
    const m = parseFloat(montant.replace(',', '.'));
    if (isNaN(m) || m <= 0) return;
    setLoading(true);
    try {
      await onConfirmer(m, reference);
      onFermer();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Enregistrer un versement</h2>
        <p className="text-sm text-gray-500 mb-6">
          {commission.apporteurNom} — {commission.trimestre}
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Montant versé (€)
            </label>
            <input
              type="number"
              value={montant}
              onChange={(e) => setMontant(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              step="0.01"
              min="0.01"
            />
            <p className="text-xs text-gray-400 mt-1">
              Reste à verser : {formatEur(commission.resteAVerser)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Référence virement (optionnel)
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Ex: VIR-2026-T1-LAZARSKI"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onFermer}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirmer}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Enregistrement...' : 'Confirmer le versement'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// ONGLET GUIDE
// ============================================

const ETAPES_GUIDE = [
  {
    numero: 1,
    titre: "Vérifier les abonnés sans apporteur",
    description: "Avant toute génération, consulter le bandeau d'alerte en haut de la page. S'il signale des abonnés actifs sans apporteur lié, utiliser la liaison manuelle dans la fiche apporteur concernée pour les rattacher. Un abonné actif non rattaché n'est pas inclus dans le calcul.",
    alerte: true,
    alerteTexte: "Point de vigilance critique : un oubli ici fausse le calcul trimestriel et la commission est manquée pour ce trimestre.",
    icone: UserX,
  },
  {
    numero: 2,
    titre: 'Générer le calcul trimestriel',
    description: "Sélectionner l'apporteur et le trimestre dans le bloc de génération, vérifier l'aperçu (montant + nombre de clients actifs), puis cliquer sur Générer. Si un trimestre existait déjà, il est recalculé avec les données actuelles.",
    alerte: false,
    alerteTexte: '',
    icone: CalendarDays,
  },
  {
    numero: 3,
    titre: 'Vérifier le détail client par client',
    description: "Déplier la commission générée et contrôler chaque ligne : formule, statut abonnement, CA HT et montant de commission. Si une donnée est erronée, la corriger dans Firestore avant de passer à l'étape suivante.",
    alerte: false,
    alerteTexte: '',
    icone: ListChecks,
  },
  {
    numero: 4,
    titre: 'Effectuer le virement et marquer comme versé',
    description: "Réaliser le virement depuis le compte bancaire DOM TECH & SERVICES. Revenir ici, cliquer sur Verser, saisir le montant exact et la référence virement pour traçabilité.",
    alerte: false,
    alerteTexte: '',
    icone: Euro,
  },
  {
    numero: 5,
    titre: "L'apporteur est notifié instantanément",
    description: "Dès le marquage comme versé, la commission apparaît dans l'espace de l'apporteur avec le statut Versé, le montant et la référence. Aucune action supplémentaire n'est nécessaire.",
    alerte: false,
    alerteTexte: '',
    icone: BadgeCheck,
  },
];

const OngletGuide: React.FC = () => (
  <div className="space-y-6">
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
      <h2 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
        <BookOpen className="w-4 h-4" />
        Rappel contrat apporteur
      </h2>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div className="bg-white rounded-lg p-3 border border-indigo-100">
          <p className="text-xs text-indigo-400 uppercase font-medium mb-1">Taux</p>
          <p className="font-bold text-indigo-800">25% du CA HT récurrent</p>
        </div>
        <div className="bg-white rounded-lg p-3 border border-indigo-100">
          <p className="text-xs text-indigo-400 uppercase font-medium mb-1">Rythme</p>
          <p className="font-bold text-indigo-800">Trimestriel</p>
        </div>
        <div className="bg-white rounded-lg p-3 border border-indigo-100">
          <p className="text-xs text-indigo-400 uppercase font-medium mb-1">Fenêtre attribution</p>
          <p className="font-bold text-indigo-800">90 jours</p>
        </div>
      </div>
      <p className="text-xs text-indigo-500 mt-3">
        L'apporteur doit émettre une facture pour chaque versement. La commission est calculée sur le CA HT hors TVA, sur la base de l'abonnement actif au moment du calcul.
      </p>
    </div>

    <div className="space-y-3">
      {ETAPES_GUIDE.map((etape, index) => {
        const Icone = etape.icone;
        return (
          <div key={etape.numero} className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">
                {etape.numero}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Icone className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                  <h3 className="font-semibold text-gray-800">{etape.titre}</h3>
                </div>
                <p className="text-sm text-gray-600">{etape.description}</p>
                {etape.alerte && (
                  <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">{etape.alerteTexte}</p>
                  </div>
                )}
              </div>
              {index < ETAPES_GUIDE.length - 1 && (
                <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-2" />
              )}
            </div>
          </div>
        );
      })}
    </div>

    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
      <p className="text-sm text-gray-600">
        <span className="font-semibold text-gray-800">Facturation :</span> L'apporteur émet sa facture avant ou après le virement — les deux sont acceptables comptablement. Conserver la facture dans Dougs pour le rapprochement.
      </p>
    </div>
  </div>
);

// ============================================
// PAGE PRINCIPALE
// ============================================

type Onglet = 'commissions' | 'guide';

const DashboardCommissionsPage: React.FC = () => {
  const [ongletActif, setOngletActif] = useState<Onglet>('commissions');
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [apporteurs, setApporteurs] = useState<InfoResponsable[]>([]);
  const [usersSansApporteur, setUsersSansApporteur] = useState<
    { userId: string; email: string; displayName: string; formule: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [trimestreSelectionne, setTrimestreSelectionne] = useState(trimestreActuel());
  const [apporteurSelectionne, setApporteurSelectionne] = useState<string>('alain');
  const [commissionModal, setCommissionModal] = useState<Commission | null>(null);
  const [apercu, setApercu] = useState<{ montantDu: number; nbClients: number } | null>(null);
  const [filtreStatut, setFiltreStatut] = useState<StatutCommission | 'tous'>('tous');

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const [comms, resps, sansApporteur] = await Promise.all([
        chargerCommissions(),
        chargerResponsables(),
        chargerUsersActifsSansApporteur(),
      ]);
      setCommissions(comms);
      const apporteursUniquement = resps.filter((r) => r.identifiant !== 'dominique');
      setApporteurs(apporteursUniquement);
      setUsersSansApporteur(sansApporteur);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  useEffect(() => {
    let annule = false;
    const calculer = async () => {
      try {
        const result = await calculerCommissionsTrimestre(trimestreSelectionne, apporteurSelectionne);
        if (!annule) {
          setApercu({
            montantDu: result.montantDu,
            nbClients: result.details.filter((d) => d.statutAbonnement === 'actif').length,
          });
        }
      } catch {
        if (!annule) setApercu(null);
      }
    };
    calculer();
    return () => { annule = true; };
  }, [trimestreSelectionne, apporteurSelectionne]);

  const handleGenerer = async () => {
    const apporteurInfo = apporteurs.find((a) => a.identifiant === apporteurSelectionne);
    if (!apporteurInfo) return;
    setGenerationLoading(true);
    try {
      await enregistrerCommission(
        trimestreSelectionne,
        apporteurSelectionne,
        apporteurInfo.nomComplet
      );
      await charger();
    } finally {
      setGenerationLoading(false);
    }
  };

  const handleMarquerVerse = async (montant: number, reference: string) => {
    if (!commissionModal) return;
    await marquerCommissionVersee(commissionModal.id, montant, reference);
    await charger();
  };

  const commissionsFiltrees = commissions.filter((c) => {
    if (filtreStatut !== 'tous' && c.statut !== filtreStatut) return false;
    return true;
  });

  const totalDu = commissions.filter((c) => c.statut !== 'verse').reduce((s, c) => s + c.resteAVerser, 0);
  const totalVerse = commissions.filter((c) => c.statut === 'verse').reduce((s, c) => s + c.montantVerse, 0);
  const nbDu = commissions.filter((c) => c.statut === 'du' || c.statut === 'partiel').length;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Commissions apporteurs</h1>
        <p className="text-sm text-gray-500 mt-1">
          Taux : {Math.round(TAUX_COMMISSION * 100)}% du CA HT récurrent — versement trimestriel
        </p>
      </div>

      {/* Bandeau alerte — visible sur les deux onglets */}
      <BandeauAlerteSansApporteur users={usersSansApporteur} />

      {/* Onglets */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setOngletActif('commissions')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            ongletActif === 'commissions'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Coins className="w-4 h-4" />
          Commissions
        </button>
        <button
          onClick={() => setOngletActif('guide')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            ongletActif === 'guide'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          Guide
        </button>
      </div>

      {ongletActif === 'guide' && <OngletGuide />}

      {ongletActif === 'commissions' && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">À verser</p>
                <p className="text-xl font-bold text-amber-700">{formatEur(totalDu)}</p>
                <p className="text-xs text-gray-400">{nbDu} trimestre{nbDu > 1 ? 's' : ''} en attente</p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total versé</p>
                <p className="text-xl font-bold text-emerald-700">{formatEur(totalVerse)}</p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Apporteurs actifs</p>
                <p className="text-xl font-bold text-indigo-700">{apporteurs.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
            <h2 className="font-semibold text-indigo-900 mb-4 flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              Générer / recalculer un trimestre
            </h2>

            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-indigo-700 mb-1">Apporteur</label>
                <select
                  value={apporteurSelectionne}
                  onChange={(e) => setApporteurSelectionne(e.target.value)}
                  className="border border-indigo-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {apporteurs.map((a) => (
                    <option key={a.identifiant} value={a.identifiant}>{a.nomComplet}</option>
                  ))}
                  {apporteurs.length === 0 && (
                    <option value="alain">Alain LAZARSKI</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-indigo-700 mb-1">Trimestre</label>
                <select
                  value={trimestreSelectionne}
                  onChange={(e) => setTrimestreSelectionne(e.target.value)}
                  className="border border-indigo-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {trimestresDisponibles().map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {apercu && (
                <div className="text-sm text-indigo-800 bg-white border border-indigo-200 rounded-lg px-3 py-2">
                  <span className="font-semibold">{formatEur(apercu.montantDu)}</span>
                  <span className="text-indigo-500 ml-1">
                    — {apercu.nbClients} client{apercu.nbClients > 1 ? 's' : ''} actif{apercu.nbClients > 1 ? 's' : ''}
                  </span>
                </div>
              )}

              <button
                onClick={handleGenerer}
                disabled={generationLoading || apporteurs.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${generationLoading ? 'animate-spin' : ''}`} />
                {generationLoading ? 'Calcul...' : 'Générer'}
              </button>
            </div>

            <p className="text-xs text-indigo-500 mt-3">
              Si un trimestre existe déjà, il sera recalculé avec les données actuelles.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {(['tous', 'du', 'partiel', 'verse'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setFiltreStatut(s)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filtreStatut === s
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s === 'tous' ? 'Tous' : LABELS_STATUT_COMMISSION[s]}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Chargement...
            </div>
          ) : commissionsFiltrees.length === 0 ? (
            <div className="text-center py-12 bg-white border border-dashed border-gray-200 rounded-xl">
              <Coins className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Aucune commission enregistrée</p>
              <p className="text-sm text-gray-400 mt-1">
                Génère un trimestre ci-dessus pour commencer.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {commissionsFiltrees.map((c) => (
                <CarteCommission
                  key={c.id}
                  commission={c}
                  onMarquerVerse={(comm) => setCommissionModal(comm)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {commissionModal && (
        <ModalVersement
          commission={commissionModal}
          onConfirmer={handleMarquerVerse}
          onFermer={() => setCommissionModal(null)}
        />
      )}
    </div>
  );
};

export default DashboardCommissionsPage;
